import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import RefreshIcon from '@mui/icons-material/Refresh';
import Fab from '@mui/material/Fab';
import Zoom from '@mui/material/Zoom';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';
import RoomIcon from '@mui/icons-material/Room';
import { SensorLatest, SubSensorType, VisibleSubSensor } from '../../services/api';
import { getBleLocSwitchStatus, getSensorsById } from '../../state/selectors';
import useGlobalStyles from '../../styles/index';
import { GatewayVisibleSubsensors } from '../SensorConfig/helpers';
import InstallProcess from './InstallProcess';
import { isDataExpired } from '../../utils/functions';
import SensorIcon from '../../styles/icons/SensorIcon';
import { sensorTypeDetails } from '../../utils/sensorProperties';
import LocateContainer from './LocateContainer';
import { setBleLocSwitchStatus } from '../../state/actions';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { SensorPositionData } from '../Map/types';

export enum InstallSteps {
  Locate = 'Locate',
  Position = 'Position',
  Name = 'Name',
  Confirm = 'Confirm',
}

export const steps = [
  InstallSteps.Locate,
  InstallSteps.Position,
  InstallSteps.Name,
  InstallSteps.Confirm,
];

interface InstallContainerProps {
  selectedSensorId: string;
  clearSelectedSensor: () => void;
  handleRefresh: () => void;
  gatewayVisibleSubsensors: GatewayVisibleSubsensors[] | undefined;
}

function InstallContainer({
  selectedSensorId,
  clearSelectedSensor,
  handleRefresh,
  gatewayVisibleSubsensors,
}: InstallContainerProps): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { classes: globalClasses } = useGlobalStyles();
  const allowAutoLocSwitch = useAppSelector(getBleLocSwitchStatus);
  const sensorsById = useAppSelector(getSensorsById);

  const [gatewayDetails, setGatewayDetails] = useState<SensorLatest>();
  const [selectedVisibleSubsensor, setSelectedVisibleSubsensor] = useState<VisibleSubSensor>();
  const [activeStep, setActiveStep] = useState(0);
  const [position, setPosition] = useState<SensorPositionData>();

  useEffect(() => {
    const sensorName = sensorsById.get(selectedSensorId)?.name ?? selectedSensorId;
    // only navigate on landing step, otherwise sensor will have name in the final step after confirmation
    if (sensorName !== selectedSensorId && activeStep === 0) {
      navigate(`/sensors/${selectedSensorId}`);
    }
  }, [activeStep, navigate, selectedSensorId, sensorsById]);

  useEffect(() => {
    setSelectedVisibleSubsensor(undefined);
    const visibleGatewaySubsensor = gatewayVisibleSubsensors?.find(
      (gatewaySensors) => gatewaySensors.gatewayId === gatewayDetails?.id
    );
    const selectedSubsensor = visibleGatewaySubsensor?.visibleSubsensors.find(
      (sensor) => sensor.suggested_id === selectedSensorId
    );

    if (selectedSubsensor && !isDataExpired(selectedSubsensor.last_seen))
      setSelectedVisibleSubsensor(selectedSubsensor);
  }, [gatewayDetails, gatewayVisibleSubsensors, selectedSensorId]);

  const mapPositionChangeHandler = (posData?: SensorPositionData) => {
    if (posData) {
      const newPosition = { ...position };
      newPosition.lat = posData?.lat ?? 0;
      newPosition.lng = posData?.lng ?? 0;
      newPosition.polygon = posData?.polygon ?? '';
      if (selectedVisibleSubsensor?.type === SubSensorType.OccSignatures) {
        newPosition.azimuth = position?.azimuth ?? 0;
        newPosition.height = position?.height ?? 2.4;
      }
      setPosition(newPosition);
    } else setPosition(posData);
  };

  const Icon = selectedVisibleSubsensor
    ? sensorTypeDetails[selectedVisibleSubsensor.type]?.icon ?? SensorIcon
    : SensorIcon;

  return (
    <Box sx={{ marginTop: '10px' }}>
      <Typography
        variant="h6"
        sx={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Icon />
        <Box sx={{ marginLeft: '10px' }}>Installing {selectedSensorId}</Box>
      </Typography>
      <Typography variant="body1" style={{ textAlign: 'center' }}>
        {selectedVisibleSubsensor?.type
          ? `${sensorTypeDetails[selectedVisibleSubsensor.type].label} -
          ${sensorTypeDetails[selectedVisibleSubsensor.type].brandName}`
          : 'Not yet visible in this location'}
      </Typography>

      <Stepper
        activeStep={activeStep}
        className={globalClasses.provisioningFormStepper}
        alternativeLabel
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel style={{ fontSize: '10px' }}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {steps[activeStep] === InstallSteps.Locate ? (
        <>
          {/* Select Location and gateway */}
          <Zoom in={steps[activeStep] === InstallSteps.Locate}>
            <Box>
              <LocateContainer
                selectedSensorId={selectedSensorId}
                clearSelectedSensor={clearSelectedSensor}
                gatewayDetails={gatewayDetails}
                setGatewayDetails={setGatewayDetails}
                gatewayVisibleSubsensors={gatewayVisibleSubsensors}
                handleNext={() => setActiveStep(activeStep + 1)}
                position={position}
                setPosition={mapPositionChangeHandler}
              />
            </Box>
          </Zoom>
          {/* Fab icons are only displayed during pre install process i.e. selecting location and gateway */}
          <Fab
            color="warning"
            size="medium"
            className={`${globalClasses.floatingBtn}`}
            style={{ right: '10px', bottom: '20px' }}
          >
            <RefreshIcon
              onClick={() => {
                setGatewayDetails(undefined);
                handleRefresh();
              }}
            />
          </Fab>
          {!allowAutoLocSwitch && (
            <Fab
              color="error"
              size="medium"
              className={`${globalClasses.floatingBtn}`}
              style={{ left: '10px', bottom: '20px' }}
            >
              <RoomIcon onClick={() => dispatch(setBleLocSwitchStatus(true))} />
            </Fab>
          )}
        </>
      ) : (
        // Collect sensor position, name and submit with details
        <InstallProcess
          gatewayDetails={gatewayDetails as SensorLatest}
          selectedSubSensor={selectedVisibleSubsensor as VisibleSubSensor}
          handleRefresh={handleRefresh}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          position={position}
          setPosition={mapPositionChangeHandler}
        />
      )}
    </Box>
  );
}

export default InstallContainer;
